/**
 * Пикер эмодзи: полный набор устройства + поиск + недавние.
 *
 * ПОЧЕМУ свой, а не emoji-mart: тот тянет ~400 КБ данных и собственный UI
 * со своей темой. Нам нужен список, группы и поиск — данные генерируются
 * из официального emoji-test.txt Unicode (scripts/build_emoji.py), UI свой,
 * новых зависимостей ноль.
 *
 * ГЛАВНОЕ РЕШЕНИЕ — ЛЕНИВАЯ ЗАГРУЗКА. Каталог весит ~310 КБ исходника
 * (3773 эмодзи). Тянуть его в основной бандл ради кнопки, которую нажимают
 * не в каждой сессии, нельзя: это удвоило бы вес приложения. Поэтому
 * `import()` внутри эффекта — Vite выносит данные в отдельный чанк, и он
 * грузится в момент ПЕРВОГО открытия пикера. Дальше лежит в модульном кэше.
 *
 * Про «эмодзи устройства»: рисует их шрифт системы, а не мы. Набор Unicode
 * 15.1 — то, что есть в свежих ОС; на старых часть символов покажется
 * прямоугольником. Это ограничение платформы, не клиента, и подменить его
 * можно только своими картинками (кастомные эмодзи — отдельная задача).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { Messages } from "../i18n/messages";
import { useT } from "../i18n";
import type { EmojiEntry } from "./emojiData";
import { palette } from "./styles";
import { color, font, radius, space } from "./theme";

/** Ключ в localStorage: недавние эмодзи, самые свежие первыми. */
const RECENT_KEY = "alg.emoji.recent";
const RECENT_MAX = 24;

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === "string") : [];
  } catch {
    // Приватный режим Safari умеет швырять из localStorage. Недавние —
    // украшение, из-за них пикер падать не должен.
    return [];
  }
}

export function rememberEmoji(ch: string) {
  try {
    const next = [ch, ...loadRecent().filter((x) => x !== ch)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* см. loadRecent */
  }
}

/**
 * Название группы эмодзи на языке интерфейса.
 *
 * В `emojiData.ts` группы записаны по-русски — это ДАННЫЕ (3782 записи),
 * и переписывать их ради перевода незачем. Переводится только подпись,
 * которая видна пользователю; незнакомая группа возвращается как есть.
 */
const EMOJI_GROUP_KEYS: Record<string, keyof Messages> = {
  "Улыбки и эмоции": "emoji.groupSmileys",
  "Люди": "emoji.groupPeople",
  "Животные и природа": "emoji.groupNature",
  "Еда и напитки": "emoji.groupFood",
  "Путешествия": "emoji.groupTravel",
  "Занятия": "emoji.groupActivities",
  "Предметы": "emoji.groupObjects",
  "Символы": "emoji.groupSymbols",
  "Флаги": "emoji.groupFlags",
};

function groupLabel(g: string, t: (k: keyof Messages) => string): string {
  const key = EMOJI_GROUP_KEYS[g];
  return key ? t(key) : g;
}

export function EmojiPicker({
  onPick,
  onClose,
  anchor,
}: {
  onPick: (ch: string) => void;
  onClose: () => void;
  /** Куда прижать окно: координаты клика. */
  anchor: { x: number; y: number };
}) {
  const t = useT();
  const [data, setData] = useState<{ list: EmojiEntry[]; groups: string[] } | null>(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState<string | null>(null);
  const [recent] = useState<string[]>(() => loadRecent());
  const boxRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let alive = true;
    void import("./emojiData")
      .then((m) => {
        if (alive) setData({ list: m.EMOJI, groups: m.EMOJI_GROUPS });
      })
      .catch(() => {
        // Чанк не доехал (обрыв сети, старый кэш после деплоя). Молчать
        // нельзя: человек ждёт список и не понимает, почему пусто.
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
  }, [data]);

  // Закрытие: клик мимо и Escape.
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const el = boxRef.current;
      if (el && e.target instanceof Node && !el.contains(e.target)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [onClose]);

  const shown = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    if (q) {
      // Поиск по имени и словам подгруппы. Пробел = И: «red heart» найдёт
      // именно красное сердце, а не всё, где есть «red».
      const words = q.split(/\s+/);
      return data.list
        .filter((e) => {
          const hay = `${e.n} ${e.k}`.toLowerCase();
          return words.every((w) => hay.includes(w));
        })
        .slice(0, 300);
    }
    const g = group ?? data.groups[0];
    return data.list.filter((e) => e.g === g).slice(0, 600);
  }, [data, query, group]);

  // Прижимаем к краям экрана: у нижних сообщений окно иначе уезжает за борт.
  const W = 320;
  const H = 360;
  const left = Math.min(Math.max(8, anchor.x), window.innerWidth - W - 8);
  const top = Math.min(Math.max(8, anchor.y), window.innerHeight - H - 8);

  const cell = (ch: string, key: string) => (
    <button
      key={key}
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => {
        rememberEmoji(ch);
        onPick(ch);
      }}
      style={{
        font: "inherit",
        fontSize: 22,
        lineHeight: 1,
        width: 34,
        height: 34,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "none",
        background: "transparent",
        borderRadius: radius.sm,
        cursor: "pointer",
      }}
      data-testid={`emoji-${ch}`}
    >
      {ch}
    </button>
  );

  return (
    <div
      ref={boxRef}
      data-testid="emoji-picker"
      style={{
        position: "fixed",
        left,
        top,
        width: W,
        height: H,
        zIndex: 80,
        display: "flex",
        flexDirection: "column",
        background: color.bg,
        border: `1px solid ${color.border}`,
        borderRadius: radius.md,
        boxShadow: "0 8px 32px rgba(20,24,31,0.24)",
        overflow: "hidden",
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div style={{ padding: space.sm, borderBottom: `1px solid ${color.border}` }}>
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("emoji.searchPlaceholder")}
          data-testid="emoji-search"
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "6px 8px",
            font: "inherit",
            fontSize: font.small,
            color: color.text,
            background: color.sidebarBg,
            border: `1px solid ${color.border}`,
            borderRadius: radius.sm,
          }}
        />
      </div>

      {!query && recent.length > 0 && (
        <div style={{ padding: "4px 6px", borderBottom: `1px solid ${color.border}` }}>
          <div style={{ fontSize: font.tiny, color: palette.textDim, padding: "2px 4px" }}>{t("emoji.recent")}</div>
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {recent.map((ch, i) => cell(ch, `r-${i}-${ch}`))}
          </div>
        </div>
      )}

      <div style={{ flex: "1 1 auto", overflowY: "auto", padding: "4px 6px" }}>
        {failed && (
          <div style={{ padding: space.md, fontSize: font.small, color: color.danger }}>
            {t("emoji.loadFailed")}
          </div>
        )}
        {!failed && !data && (
          <div style={{ padding: space.md, fontSize: font.small, color: palette.textDim }}>{t("room.loadingShort")}</div>
        )}
        {data && shown.length === 0 && (
          <div style={{ padding: space.md, fontSize: font.small, color: palette.textDim }}>{t("emoji.nothingFound")}</div>
        )}
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          {shown.map((e) => cell(e.c, e.c))}
        </div>
      </div>

      {data && !query && (
        <div
          style={{
            display: "flex",
            gap: 2,
            padding: "4px 6px",
            borderTop: `1px solid ${color.border}`,
            overflowX: "auto",
          }}
        >
          {data.groups.map((g) => {
            const active = (group ?? data.groups[0]) === g;
            // Первый эмодзи группы — её иконка: отдельный набор иконок
            // держать незачем, а подпись словом не влезет в ряд.
            const icon = data.list.find((e) => e.g === g)?.c ?? "•";
            return (
              <button
                key={g}
                type="button"
                title={groupLabel(g, t)}
                onClick={() => setGroup(g)}
                data-testid={`emoji-group-${g}`}
                style={{
                  font: "inherit",
                  fontSize: 18,
                  lineHeight: 1,
                  padding: "4px 6px",
                  border: "none",
                  borderRadius: radius.sm,
                  cursor: "pointer",
                  background: active ? color.accentSoft : "transparent",
                }}
              >
                {icon}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
